import { Repeat, Trash2 } from 'lucide-react';
import { EXPENSE_CATEGORIES } from '../../utils/expenseHelpers';

/**
 * Recurring Expenses modal — manage scheduled (weekly / monthly / yearly)
 * expenses like rent, subscriptions, etc. Pure presentation; state lives in
 * the parent.
 *
 * Extracted from Expenses.js as part of the giant-component refactor
 * (Feb 2026). Behavior is byte-for-byte equivalent.
 */
const RecurringExpenseModal = ({
  open,
  recurringForm,
  setRecurringForm,
  recurringExpenses,
  onAdd,
  onDelete,
  onClose,
}) => {
  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '16px',
          padding: '2rem',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <Repeat size={24} color="#7c3aed" />
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>Recurring Expenses</h2>
        </div>

        {/* Add New Recurring */}
        <div style={{ background: '#f9fafb', padding: '1.25rem', borderRadius: '12px', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>Add New Recurring Expense</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <input
              type="text"
              placeholder="Name (e.g., Rent)"
              value={recurringForm.name}
              onChange={(e) => setRecurringForm({ ...recurringForm, name: e.target.value })}
              data-testid="recurring-name-input"
              style={{ padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem' }}
            />
            <input
              type="number"
              placeholder="Amount"
              value={recurringForm.amount}
              onChange={(e) => setRecurringForm({ ...recurringForm, amount: e.target.value })}
              data-testid="recurring-amount-input"
              style={{ padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem' }}
            />
            <select
              value={recurringForm.category}
              onChange={(e) => setRecurringForm({ ...recurringForm, category: e.target.value })}
              data-testid="recurring-category-select"
              style={{ padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem' }}
            >
              <option value="">Select Category</option>
              {EXPENSE_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <select
              value={recurringForm.frequency}
              onChange={(e) => setRecurringForm({ ...recurringForm, frequency: e.target.value })}
              data-testid="recurring-frequency-select"
              style={{ padding: '0.625rem', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem' }}
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
          <button
            onClick={onAdd}
            data-testid="add-recurring-button"
            style={{
              marginTop: '1rem',
              padding: '0.625rem 1.25rem',
              background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Add Recurring Expense
          </button>
        </div>

        {/* Existing Recurring */}
        <div>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>Active Recurring Expenses</h3>
          {recurringExpenses.length === 0 ? (
            <p style={{ color: '#6b7280', textAlign: 'center', padding: '1rem' }}>No recurring expenses set up yet</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {recurringExpenses.map((exp) => (
                <div
                  key={exp.id}
                  data-testid={`recurring-item-${exp.id}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1rem',
                    background: '#f9fafb',
                    borderRadius: '10px',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600 }}>{exp.name}</div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                      ${exp.amount} • {exp.frequency} • {exp.category}
                    </div>
                  </div>
                  <button
                    onClick={() => onDelete(exp.id)}
                    data-testid={`delete-recurring-${exp.id}`}
                    style={{
                      padding: '0.5rem',
                      background: '#fee2e2',
                      color: '#dc2626',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          data-testid="close-recurring-modal"
          style={{
            marginTop: '1.5rem',
            width: '100%',
            padding: '0.75rem',
            background: '#f3f4f6',
            color: '#374151',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default RecurringExpenseModal;
